import * as defaultSessions from './upload.session.service';
import { putBlob as defaultPutBlob } from './upload.part.transport';
import { fingerprintOf as defaultFingerprintOf } from './upload.fingerprint';
import { resumeStore as defaultStore } from './upload.resume.store';
import { inflightRegistry as defaultRegistry } from './upload.inflight.registry';
import { partSlots as defaultSlots } from './upload.part.slots';
import { engineState as defaultEngineState } from './upload.engine.state';
import clientLogger from '../logging/client.logger';

const MS_PER_SECOND = 1000;
const PERCENT = 100;
const STATUS_NOT_FOUND = 404;
const STATUS_CONFLICT = 409;
const REPAIR_ROUNDS = 2;
const ENGINE_OFF_REASON = 'UploadEngineOff';
const PARTS_MISSING_REASON = 'PartsMissing';
const COMPLETING_REASON = 'UploadCompleting';
const STATUS_SERVER_MIN = 500;
const STORAGE_SWITCHED_REASON = 'StorageSwitched';
const MAX_TIER_SWITCHES = 2;
const STALL_KIND = 'stall';
const API_FAILURE_KIND = 'api';
const STALLED_REPORT_KIND = 'stalled';
const REPORTED_KINDS = Object.freeze(['server', 'network', STALL_KIND]);
const SINGLE_STRATEGY = 'single';
const COMPLETING_STATUS = 'completing';
const DEFAULT_FAILURE = 'Não foi possível enviar o arquivo.';
const FALLBACK_TYPE = 'application/octet-stream';
const LOG_CONTEXT = 'upload:engine';
const SLOW_STEPS_MS = Object.freeze([15000, 30000]);
const SLOW_STEADY_MS = 60000;
const WAKE_TIMER = 'timer';
const WAKE_ONLINE = 'online';
const WAKE_VISIBLE = 'visible';
const SLOW_STATE = 'slow';
const RECOVERED_MESSAGE = 'recovered';

const realWait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const realSlowWait = (ms) => new Promise(resolve => {
    const pending = { timer: null, listeners: [] };
    const settle = (reason) => {
        clearTimeout(pending.timer);
        pending.listeners.forEach(([target, name, handler]) => target.removeEventListener(name, handler));
        resolve(reason);
    };
    const listen = (target, name, reason, accepts = () => true) => {
        const handler = () => accepts() && settle(reason);
        target.addEventListener(name, handler);
        pending.listeners.push([target, name, handler]);
    };
    pending.timer = setTimeout(() => settle(WAKE_TIMER), ms);
    listen(window, 'online', WAKE_ONLINE);
    listen(document, 'visibilitychange', WAKE_VISIBLE, () => document.visibilityState === 'visible');
});

const answerOf = (error) => (error && error.response && error.response.data) || {};

const isEngineOff = (error) => Boolean(error) && error.status === STATUS_NOT_FOUND && [ENGINE_OFF_REASON, undefined].includes(answerOf(error).reason);

const missingPartsOf = (error) => (error && error.status === STATUS_CONFLICT && answerOf(error).reason === PARTS_MISSING_REASON ? answerOf(error).missingParts : null);

const connectionType = () => (window.navigator.connection && window.navigator.connection.effectiveType) || null;

const waitOnline = (context) => {
    if (window.navigator.onLine !== false) return Promise.resolve();
    context.metrics.offlinePauses += 1;
    context.onState({ state: 'offline' });
    return new Promise(resolve => window.addEventListener('online', resolve, { once: true }));
};

const partBytes = ({ descriptor, file }, partNumber) => (descriptor.strategy === SINGLE_STRATEGY
    ? file.size
    : Math.min(descriptor.partSize, file.size - (partNumber - 1) * descriptor.partSize));

const blobOf = ({ descriptor, file }, partNumber) => {
    if (descriptor.strategy === SINGLE_STRATEGY) return file;
    const start = (partNumber - 1) * descriptor.partSize;
    return file.slice(start, Math.min(start + descriptor.partSize, file.size));
};

const failureReportOf = (failure) => {
    if (!failure || !REPORTED_KINDS.includes(failure.kind)) return null;
    return { kind: failure.kind === STALL_KIND ? STALLED_REPORT_KIND : failure.kind, status: failure.status || null };
};

const isSwitched = (error) => Boolean(error) && error.status === STATUS_CONFLICT && answerOf(error).reason === STORAGE_SWITCHED_REASON;

const isTransientApiError = (error) => !error || !error.status || error.status >= STATUS_SERVER_MIN;

const apiFailureOf = (error) => (isTransientApiError(error) ? { kind: API_FAILURE_KIND, status: (error && error.status) || null } : error);

const urlFor = async ({ descriptor, deps }, partNumber, attempt) => {
    if (attempt.first && descriptor.url) return descriptor.url;
    const [signed] = await deps.sessions.partUrls(descriptor.sessionId, [partNumber], failureReportOf(attempt.lastFailure));
    return signed.url;
};

const progressed = (context, partNumber, loaded) => {
    context.slowing = false;
    context.onBytes(partNumber, loaded);
};

const recovered = (context, partNumber) => {
    if (context.slowing) progressed(context, partNumber, partBytes(context, partNumber));
};

const putOnce = (context, partNumber, url) => context.deps.slots.run(() => context.deps.putBlob({
    url,
    blob: blobOf(context, partNumber),
    contentType: context.descriptor.strategy === SINGLE_STRATEGY ? context.file.type || FALLBACK_TYPE : undefined,
    stallMs: context.descriptor.stallSeconds * MS_PER_SECOND,
    onProgress: (loaded) => progressed(context, partNumber, loaded),
})).then(sent => {
    recovered(context, partNumber);
    return sent;
});

const slowDelayOf = (round) => (round < SLOW_STEPS_MS.length ? SLOW_STEPS_MS[round] : SLOW_STEADY_MS);

const slowRound = async (context, round) => {
    if (!context.slowing) {
        context.slowing = true;
        context.onState({ state: SLOW_STATE });
    }
    context.metrics.slowCycles += 1;
    const reason = await context.deps.slowWait(slowDelayOf(round));
    context.metrics.wakeups[reason] = (context.metrics.wakeups[reason] || 0) + 1;
};

const nextAttempt = async (context, failure, attempt) => {
    if (failure.kind === 'expired' && attempt.renewals < context.descriptor.retry.max) {
        context.metrics.urlRenewals += 1;
        return { ...attempt, renewals: attempt.renewals + 1, lastFailure: null };
    }
    if (failure.kind === 'http' || failure.kind === 'expired') throw failure;
    if (attempt.tries >= context.descriptor.retry.max) {
        await slowRound(context, attempt.slow);
        if (context.halted) throw failure;
        return { ...attempt, slow: attempt.slow + 1, lastFailure: failure };
    }
    context.metrics.retries += 1;
    await context.deps.wait(context.descriptor.retry.baseMs * (2 ** attempt.tries));
    return { ...attempt, tries: attempt.tries + 1, lastFailure: failure };
};

const sendPart = async (context, partNumber, attempt = { tries: 0, slow: 0, renewals: 0, first: true, lastFailure: null }) => {
    await waitOnline(context);
    return urlFor(context, partNumber, attempt)
        .catch(error => Promise.reject(Object.assign(apiFailureOf(error), { fromApi: true })))
        .then(url => putOnce(context, partNumber, url))
        .catch(async failure => {
            context.onBytes(partNumber, 0);
            if (context.halted || (failure.fromApi && failure.kind !== API_FAILURE_KIND)) throw failure;
            return sendPart(context, partNumber, { ...(await nextAttempt(context, failure, attempt)), first: false });
        });
};

const runQueue = async (context, partNumbers) => {
    const queue = [...partNumbers];
    const failures = [];
    context.deps.slots.limitTo(context.descriptor.concurrency);
    const worker = async () => {
        while (queue.length && !context.halted) await sendPart(context, queue.shift());
    };
    const halt = (failure) => {
        context.halted = true;
        context.metrics.failedParts += 1;
        failures.push(failure);
    };
    await Promise.all(Array.from({ length: Math.min(context.descriptor.concurrency, queue.length) }, () => worker().catch(halt)));
    if (failures.length) throw failures.find(isSwitched) || failures[0];
};

const isCompletionPending = (error) => {
    const status = error && error.status;
    return !status || status >= STATUS_SERVER_MIN || (status === STATUS_CONFLICT && answerOf(error).reason === COMPLETING_REASON);
};

const retryCompletion = async (context, error, rounds) => {
    const missingParts = missingPartsOf(error);
    if (missingParts && rounds.repairs > 0) {
        await runQueue(context, missingParts);
        return { ...rounds, repairs: rounds.repairs - 1 };
    }
    if (!isCompletionPending(error)) throw error;
    if (window.navigator.onLine === false) {
        await waitOnline(context);
        context.onState({ state: 'confirming' });
        return rounds;
    }
    if (rounds.waits >= context.descriptor.retry.max) {
        await slowRound(context, rounds.slow);
        return { ...rounds, slow: rounds.slow + 1 };
    }
    await context.deps.wait(context.descriptor.retry.baseMs * (2 ** rounds.waits));
    return { ...rounds, waits: rounds.waits + 1 };
};

const completeWithRepair = (context, rounds = { repairs: REPAIR_ROUNDS, waits: 0, slow: 0 }) =>
    context.deps.sessions.complete(context.descriptor.sessionId, context.metrics)
        .catch(async error => completeWithRepair(context, await retryCompletion(context, error, rounds)));

const progressReporter = ({ file, onState, now, storedBytes, isQuiet }) => {
    const sentByPart = new Map();
    const startedAt = now();
    const emit = () => {
        const sentNow = [...sentByPart.values()].reduce((sum, value) => sum + value, 0);
        const sent = storedBytes + sentNow;
        const bytesPerSecond = sentNow / Math.max((now() - startedAt) / MS_PER_SECOND, 1);
        onState({
            state: 'uploading',
            percent: Math.min(PERCENT, Math.round((sent / file.size) * PERCENT)),
            bytesPerSecond,
            secondsLeft: Math.round((file.size - sent) / Math.max(bytesPerSecond, 1)),
        });
    };
    const onBytes = (partNumber, loaded) => {
        sentByPart.set(partNumber, loaded);
        if (!isQuiet()) emit();
    };
    return { onBytes, emit };
};

const metricsOf = (resumeSessionId) => ({
    retries: 0, failedParts: 0, urlRenewals: 0, offlinePauses: 0, slowCycles: 0, wakeups: { timer: 0, online: 0, visible: 0 },
    activeMs: 0, resumes: resumeSessionId ? 1 : 0, connectionType: connectionType(),
});

const fingerprintOrNull = (deps, file) => Promise.resolve()
    .then(() => deps.fingerprintOf(file))
    .catch(() => null);

const engineOff = (deps) => {
    deps.engineState.markOff();
    return { off: true };
};

const openOrOff = async (deps, { file, targetKind, target }) => {
    if (deps.engineState.isKnownOff()) return { off: true };
    const fingerprint = await fingerprintOrNull(deps, file);
    if (!fingerprint) return { off: true };
    const request = { targetKind, target, file: { name: file.name, size: file.size, type: file.type || FALLBACK_TYPE, fingerprint } };
    return deps.sessions.open(request)
        .then(descriptor => ({ descriptor, fingerprint }))
        .catch(error => (isEngineOff(error) ? engineOff(deps) : Promise.reject(error)));
};

const pendingParts = async (context) => {
    if (context.descriptor.sessionStatus === COMPLETING_STATUS) return [];
    const all = Array.from({ length: context.descriptor.partCount }, (_, index) => index + 1);
    if (context.descriptor.strategy === SINGLE_STRATEGY) return all;
    const { parts, assembled } = await context.deps.sessions.parts(context.descriptor.sessionId);
    if (assembled) return [];
    const stored = new Set((parts || []).map(part => part.partNumber));
    return all.filter(partNumber => !stored.has(partNumber));
};

const startProgress = (context, pending) => {
    const storedBytes = context.file.size - pending.reduce((sum, partNumber) => sum + partBytes(context, partNumber), 0);
    const progress = progressReporter({ file: context.file, onState: context.onState, now: context.deps.now, storedBytes, isQuiet: () => context.slowing });
    context.onBytes = progress.onBytes;
    if (storedBytes > 0) progress.emit();
};

const transfer = async (context) => {
    const startedAt = context.deps.now();
    const pending = await pendingParts(context);
    startProgress(context, pending);
    await runQueue(context, pending);
    context.metrics.activeMs = context.deps.now() - startedAt;
    context.onState({ state: 'confirming' });
    return completeWithRepair(context);
};

const switchedSessionOf = (error) => (isSwitched(error) ? answerOf(error).session : null);

const adoptSession = async (context, descriptor) => {
    const { deps, fingerprint, file, targetKind } = context;
    const previousId = context.descriptor.sessionId;
    deps.registry.add(descriptor.sessionId);
    deps.registry.remove(previousId);
    await deps.store.remove(previousId);
    context.descriptor = descriptor;
    context.halted = false;
    context.slowing = false;
    context.switches += 1;
    await deps.store.save({ sessionId: descriptor.sessionId, fingerprint, name: file.name, size: file.size, targetKind });
    context.onState({ state: 'uploading', percent: 0, bytesPerSecond: 0, secondsLeft: 0 });
};

const transferAcrossTiers = (context) => transfer(context).catch(async (error) => {
    const next = switchedSessionOf(error);
    if (!next || context.switches >= MAX_TIER_SWITCHES) throw error;
    await adoptSession(context, next);
    return transferAcrossTiers(context);
});

const reportEvent = (context, failure, message) => clientLogger.logClientError({
    context: LOG_CONTEXT,
    message,
    payload: {
        failure: failure && (failure.kind || failure.status || null), sessionId: context.descriptor.sessionId, fileSize: context.file.size,
        fileType: context.file.type, online: window.navigator.onLine, effectiveConnectionType: connectionType(), metrics: context.metrics,
    },
});

const settleFailure = (context, failure) => {
    const outcome = { status: 'failed', message: answerOf(failure).message || DEFAULT_FAILURE, sessionId: context.descriptor.sessionId };
    reportEvent(context, failure, outcome.status);
    context.onState({ state: outcome.status, ...outcome });
    return outcome;
};

const contextOf = ({ deps, descriptor, file, onState, resumeSessionId, fingerprint, targetKind }) => ({
    deps, descriptor, file, onState, fingerprint, targetKind, switches: 0, halted: false, slowing: false, metrics: metricsOf(resumeSessionId), onBytes: () => undefined,
});

const settleSuccess = async (context, answer) => {
    await context.deps.store.remove(context.descriptor.sessionId);
    if (context.metrics.slowCycles > 0) reportEvent(context, null, RECOVERED_MESSAGE);
    context.onState({ state: 'completed', result: answer.result });
    return { status: 'completed', result: answer.result, documentId: answer.documentId, sessionId: context.descriptor.sessionId };
};

const runUpload = async (deps, { file, targetKind, target, onState = () => {}, resumeSessionId = null }) => {
    const opened = await openOrOff(deps, { file, targetKind, target });
    if (opened.off) return { engine: 'off' };
    const context = contextOf({ deps, descriptor: opened.descriptor, file, onState, resumeSessionId, fingerprint: opened.fingerprint, targetKind });
    const sessionId = context.descriptor.sessionId;
    deps.registry.add(sessionId);
    await deps.store.save({ sessionId, fingerprint: opened.fingerprint, name: file.name, size: file.size, targetKind });
    return transferAcrossTiers(context)
        .then(answer => settleSuccess(context, answer), failure => settleFailure(context, failure))
        .finally(() => deps.registry.remove(context.descriptor.sessionId));
};

const DEFAULT_DEPENDENCIES = Object.freeze({
    sessions: defaultSessions, putBlob: defaultPutBlob, fingerprintOf: defaultFingerprintOf, store: defaultStore,
    registry: defaultRegistry, slots: defaultSlots, engineState: defaultEngineState, wait: realWait, slowWait: realSlowWait, now: () => Date.now(),
});

export const createUploadEngine = (overrides = {}) => {
    const deps = { ...DEFAULT_DEPENDENCIES, ...overrides };
    return { upload: (request) => runUpload(deps, request) };
};

export const uploadEngine = createUploadEngine();
